import { FC } from 'react';
import { usePathContext } from '@new/common/hooks/usePathContext';
import {
  useGetObjectiveQuery,
  useMarkObjectiveCompletedMutation,
  useGetCourseQuery,
} from '@teachur/http-client';
import { useObjectiveCompletion } from '../hooks/useObjectiveCompletion';
import { Can } from '@new/casl';
import { Button } from '@new/common/ui';
import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { EditButton } from '@new/common/ui/EditButton/EditButton';
import { Loader } from '@new/common/ui/Loader/Loader';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { ObjectiveMediaSection } from '../components/ObjectiveVideos/ObjectiveVideos';
import { NotesPanel } from '@new/features/notes/components/NotesPanel/NotesPanel';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { toast } from 'sonner';

export const ObjectiveDetailPage: FC = () => {
  const { objectiveId, courseId, lessonId } = usePathContext();

  const { data: objective, isLoading: isObjectiveLoading } = useGetObjectiveQuery(objectiveId ?? 0);

  const { data: course } = useGetCourseQuery(courseId || 0, {
    enabled: !!courseId,
  });
  const { isCompleted } = useObjectiveCompletion(objectiveId || 0);
  const { mutate: markCompleted, isPending: isMarkingPending } = useMarkObjectiveCompletedMutation(
    lessonId,
    courseId
  );
  const requireSubscribed = useRequireSubscribed();

  const isCourseStarted = course?.status !== 'notStarted';
  const isToggleDisabled = !isCourseStarted;
  const isExamPassed = course?.quizCompleted || course?.courseExam?.passed;

  if (!objectiveId) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <EmptyState message="Objective ID is missing" />
      </div>
    );
  }

  if (isObjectiveLoading) {
    return <Loader />;
  }

  if (!objective || typeof objective === 'number') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <EmptyState message="Objective not found" />
      </div>
    );
  }

  const onClick = requireSubscribed(() => {
    if (isExamPassed) {
      return;
    }
    if (!course) {
      return;
    }

    if (course.status === 'completed') {
      toast.warning('This course is completed, so objectives can no longer be changed.');
      return;
    }

    if (course.isPaused) {
      toast.warning('This course is paused. Resume the course to continue.');
      return;
    }

    markCompleted({ objectiveId: objective.id, isCompleted });
  });

  return (
    <div className="relative w-full h-full bg-[#FAFDFF] py-5 px-7 flex flex-col gap-[32px]">
      <PageHeader
        title={objective.name}
        description={objective.description}
        badge={{
          text: 'Objectives',
          bgColor: 'var(--secondary-B100)',
          textColor: 'var(--secondary-B900)',
        }}
      />
      <Can I="update" a="Objective">
        <EditButton />
      </Can>

      {/* <MiniQuiz
        question="What is the meaning of philosophy?"
        answers={[
          {
            id: 1,
            text: 'Lorem ipsum dolor sit amet consectetur. Scelerisque quis pharetra curabitur porta in posuere sed quam lectus. Orci aliquet diam tincidunt aliquam odio.',
            isCorrect: false,
          },
          {
            id: 2,
            text: 'Lorem ipsum dolor sit amet consectetur. Scelerisque quis pharetra curabitur porta in posuere sed quam lectus. Orci aliquet diam tincidunt aliquam odio.',
            isCorrect: true,
          },
          {
            id: 3,
            text: 'Lorem ipsum dolor sit amet consectetur. Scelerisque quis pharetra curabitur porta in posuere sed quam lectus. Orci aliquet diam tincidunt aliquam odio.',
            isCorrect: false,
          },
        ]}
      /> */}

      <ObjectiveMediaSection
        media={objective?.media}
        objectiveName={objective?.name}
        objectiveId={objectiveId}
      />

      <Can I="start" a="Course">
        {!isExamPassed && (
          <div className="flex-col items-center gap-[8px]">
            <Button
              variant={isCompleted ? 'outline' : 'primary'}
              className="w-full cursor-pointer"
              onClick={onClick}
              disabled={isMarkingPending || isToggleDisabled}
            >
              {isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
            </Button>
          </div>
        )}

        <NotesPanel relId={objectiveId} rel="objective" />
      </Can>
    </div>
  );
};
